// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function MagicEraserAiTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Magic Eraser AI: Photo Cleaner"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Magic
        Eraser AI: Photo Cleaner” (the “App”). By downloading, installing,
        accessing, or using the App, you agree to be bound by these Terms. If
        you do not agree, you must uninstall the App and discontinue all use.
        Where required by platform rules or local law, additional terms may
        apply; in case of conflict with App Store or Google Play terms, the
        platform terms may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        The App provides tools to remove unwanted objects from photos using
        image editing and AI-powered inpainting. You can import a photo, mark
        areas to erase, and generate a cleaned version to view, save, or share.
        Free use may be limited to a small number of exports, while a premium
        subscription may unlock additional or unlimited edits, higher-quality
        output, and other enhancements. The App is intended for general
        consumer photo editing only and does not provide legal, medical, or any
        other professional advice.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) User Content and Responsibility</div>
      <p>
        The photos, images, and other materials you import or create with the
        App (“User Content”) remain yours or your licensors’. You are solely
        responsible for ensuring that you have all necessary rights and
        permissions to use, edit, and share User Content, including any
        permissions from individuals who appear in your photos and any
        third-party rights holders. You must not use the App to process or
        distribute content that is illegal, abusive, infringing, or otherwise
        violates applicable law or the rights of others.
      </p>
      <p>
        You are also responsible for how you choose to present edited content.
        For example, removing elements from images may change context; you
        should not use edited photos to mislead others in a way that could cause
        harm, fraud, or reputational damage.
      </p>

      <div className={s.title}>5) Local Processing and Storage</div>
      <p>
        In the current version, object removal and related image processing are
        intended to be performed on your device using local computation. We do
        not upload your original photos to our own servers solely for object
        removal or cleaning. Processed results and temporary data may be stored
        locally in the App&apos;s storage area and may be cleared if you delete
        the App or its data.
      </p>
      <p>
        If future versions introduce optional cloud-based features (for example,
        backups or remote processing), those features will be described in the
        App and in the Privacy Policy, and may require additional notices or
        consents where required by law.
      </p>

      <div className={s.title}>6) Free Use, Limits, and Watermarks</div>
      <p>
        The App may allow free usage with certain limits, such as a maximum
        number of exports, restrictions on resolution, or the presence of
        watermarks or branding on output images. These limits and conditions
        will be described in the App and may be changed over time. Export
        counters or limits may be stored locally and/or associated with your
        account or device. Deleting or reinstalling the App does not guarantee
        reset of such limits.
      </p>

      <div className={s.title}>7) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features may be offered through auto-renewable subscriptions or
        other in-app purchases (“Subscriptions”), such as weekly, monthly, or
        yearly plans. Premium plans may include benefits such as unlimited or
        increased edits, HD or higher-resolution exports, removal of watermarks,
        or additional tools described in the App or on the paywall screen. Exact
        pricing, plan names, trial length, and availability can vary by region
        and may change over time.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are determined by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>8) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features. An internet connection may be required to validate or refresh
        entitlements after long offline periods. If platform billing reverses,
        refunds, or flags a transaction as fraudulent, related entitlements may
        be limited, suspended, or revoked.
      </p>

      <div className={s.title}>9) Generated Output and Limitations</div>
      <p>
        The App uses algorithms and, where applicable, AI models to remove
        objects and fill in backgrounds. Output quality may vary depending on
        the image, markings, and technical constraints. Results may include
        artifacts, distortions, or incomplete removal. We do not guarantee that
        the App will perfectly remove any object, restore backgrounds, or
        produce professional-grade retouching suitable for all use cases.
      </p>
      <p>
        You are responsible for reviewing edited images before sharing or using
        them and for ensuring they meet your needs (for example, for social
        media, printing, or commercial use). The App does not verify or certify
        that edited content is authentic or unaltered.
      </p>

      <div className={s.title}>10) Acceptable Use and Prohibited Content</div>
      <p>
        You agree to use the App only for lawful purposes and not to:
      </p>
      <ul>
        <li>
          upload or process images that are illegal, exploitative, hateful, or
          otherwise prohibited by applicable law or platform rules;
        </li>
        <li>
          use edits to deceive or impersonate others, create fraudulent
          documents, or mislead in ways that could cause harm;
        </li>
        <li>
          infringe intellectual property rights, privacy rights, or other rights
          of any person, including by editing and sharing photos without
          appropriate permission;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          bypass technical protections, usage limits, or paywalls separating
          free and premium features.
        </li>
      </ul>

      <div className={s.title}>11) Intellectual Property</div>
      <p>
        Subject to your rights in User Content, all software, models, filters,
        user interface designs, logos, trade names, and other materials provided
        with the App are owned by the publisher or its licensors and are
        protected by intellectual property laws. Subject to these Terms and any
        Subscription limitations, you are granted a limited, non-exclusive,
        non-transferable, revocable license to use the App and its features
        solely for personal or permitted professional photo editing.
      </p>
      <p>
        You grant us and our service providers a limited, non-exclusive,
        worldwide, royalty-free license to process User Content as necessary to
        operate and improve the App, including performing object removal,
        generating previews, saving drafts, and storing exports in the App&apos;s
        local storage.
      </p>

      <div className={s.title}>12) Third-Party Services and Libraries</div>
      <p>
        The App may use third-party services and libraries for image processing,
        analytics, crash reporting, subscriptions, and storage management.
        Examples can include system APIs, GPU frameworks, Firebase Analytics or
        Crashlytics, and RevenueCat. Technical data and usage information may be
        shared with these providers to operate, secure, and improve the App.
        These providers act under their own terms and privacy policies, and we
        require them by contract to handle data appropriately and only for
        permitted purposes.
      </p>

      <div className={s.title}>13) Analytics and Performance Monitoring</div>
      <p>
        We may use analytics tools to understand how the App is used, detect
        problems, and improve reliability and user experience. Typical events
        may include app launches, screen views, export attempts, paywall views,
        and purchases. Aggregated or de-identified data may be used for product
        decisions. Where required by law, we will obtain necessary permissions
        or provide settings to manage analytics preferences.
      </p>

      <div className={s.title}>14) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and
        non-infringement. We do not warrant that the App will meet your
        requirements, that edits will always be successful or free of artifacts,
        or that the App will be available without interruption or error.
      </p>

      <div className={s.title}>15) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of photos, drafts, or exports), or business
        interruption arising from or related to your use of or inability to use
        the App. Our total aggregate liability to you for direct damages arising
        out of or relating to the App will be limited to the amount you paid for
        the App or Subscriptions during the twelve (12) months prior to the
        claim, or the minimum amount permitted by applicable law if greater.
        Some jurisdictions do not allow certain limitations or exclusions, so
        parts of this section may not apply to you.
      </p>

      <div className={s.title}>16) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your User Content, your breach of
        these Terms, or your violation of any rights of a third party.
      </p>

      <div className={s.title}>17) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>18) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Magic Eraser AI: Photo Cleaner, which explains how information is
        collected, used, stored, and shared, including any analytics or
        technical data. In case of conflict between these Terms and the Privacy
        Policy regarding data processing, the Privacy Policy will guide how we
        handle personal data, while these Terms govern your use of the App and
        its features. You should review the Privacy Policy regularly to
        understand current practices.
      </p>

      <div className={s.title}>19) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>20) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
